import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stethoscope, LogOut, Calendar, Clock, Users, FileText,
  Paperclip, User, Activity, ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { fmtRange12 } from '../../utils/time';

const portalApi = axios.create({ baseURL: '/api/family-portal/portal' });
portalApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('portal_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
portalApi.interceptors.response.use(r => r, err => {
  if (err.response?.status === 401) {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_member');
    window.location.href = '/portal/login';
  }
  return Promise.reject(err);
});

const STATUS_COLOR = {
  completed: 'bg-emerald-100 text-emerald-700',
  booked:    'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  pending:   'bg-amber-100 text-amber-700',
};

const SHIFT_COLORS = [
  'bg-blue-100 text-blue-700 border-l-2 border-blue-400',
  'bg-emerald-100 text-emerald-700 border-l-2 border-emerald-400',
  'bg-purple-100 text-purple-700 border-l-2 border-purple-400',
  'bg-orange-100 text-orange-700 border-l-2 border-orange-400',
];
function shiftColor(type) {
  let h = 0;
  for (let i = 0; i < (type || '').length; i++) h = ((h << 5) - h + type.charCodeAt(i)) | 0;
  return SHIFT_COLORS[Math.abs(h) % SHIFT_COLORS.length];
}

const TABS = [
  { id: 'timeline',  label: 'Timeline',       icon: Activity },
  { id: 'shifts',    label: 'Upcoming Shifts', icon: Calendar },
  { id: 'staff',     label: 'Staff',           icon: Users },
  { id: 'notes',     label: 'Notes',           icon: FileText },
  { id: 'documents', label: 'Documents',       icon: Paperclip },
  { id: 'profile',   label: 'Profile',         icon: User },
];

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [tab, setTab]           = useState('timeline');
  const [member, setMember]     = useState(null);
  const [settings, setSettings] = useState({});
  const [data, setData]         = useState({});
  const [loading, setLoading]   = useState({});

  useEffect(() => {
    if (!localStorage.getItem('portal_token')) { navigate('/portal/login', { replace: true }); return; }
    portalApi.get('/me').then(r => {
      // /portal/me sends the participant's name as client_name; the header read clientName, so it
      // said "Viewing care for" with no name. Normalised once here for both places that show it.
      const m = r.data.member || null;
      setMember(m ? { ...m, clientName: m.client_name || m.clientName || '' } : null);
      setSettings(r.data.settings || {});
    }).catch(() => navigate('/portal/login', { replace: true }));
  }, []);

  const load = async (key, endpoint) => {
    if (data[key] !== undefined) return;
    setLoading(l => ({ ...l, [key]: true }));
    try {
      const r = await portalApi.get(endpoint);
      setData(d => ({ ...d, [key]: r.data }));
    } catch (e) {
      if (e.response?.status === 403) setData(d => ({ ...d, [key]: 'forbidden' }));
      else setData(d => ({ ...d, [key]: [] }));
    } finally { setLoading(l => ({ ...l, [key]: false })); }
  };

  useEffect(() => {
    if (!member) return;
    const map = {
      timeline:  '/timeline',
      shifts:    '/shifts',
      staff:     '/staff',
      notes:     '/shift-notes',
      documents: '/attachments',
      profile:   '/client-profile',
    };
    load(tab, map[tab]);
  }, [tab, member]);

  const logout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_member');
    navigate('/portal/login', { replace: true });
  };

  const Spinner = () => (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const Forbidden = () => (
    <div className="text-center py-16 text-slate-400">
      <p className="text-lg font-medium">Access restricted</p>
      <p className="text-sm mt-1">Your coordinator hasn't enabled this section.</p>
    </div>
  );

  const Empty = ({ msg = 'No data available' }) => (
    <div className="text-center py-16 text-slate-400 text-sm">{msg}</div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow">
              <Stethoscope size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-900 text-sm">Rostery Family Portal</p>
              {member && (
                <p className="text-xs text-slate-400">
                  {member.clientName} · logged in as {member.name}
                </p>
              )}
            </div>
          </div>
          <button onClick={logout}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-500 transition-colors">
            <LogOut size={15}/> Logout
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Welcome card */}
        {member && (
          <div className="bg-gradient-to-r from-primary-600 to-primary-500 rounded-2xl p-5 mb-6 text-white shadow-lg">
            <p className="text-sm opacity-80">Welcome back, {member.name}</p>
            <p className="text-xl font-bold mt-0.5">Viewing care for {member.clientName}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-2xl border border-slate-200 p-1.5 mb-6 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex-1 justify-center',
                  tab === t.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-slate-500 hover:bg-slate-50'
                )}>
                <Icon size={13}/> {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">

          {/* TIMELINE */}
          {tab === 'timeline' && (
            loading.timeline ? <Spinner /> :
            data.timeline === 'forbidden' ? <Forbidden /> :
            !data.timeline?.length ? <Empty msg="No recent shifts in the last 30 days" /> : (
              <div className="divide-y divide-slate-50">
                {data.timeline.map((s, i) => (
                  <div key={s.id || i} className="flex items-start gap-4 p-4 hover:bg-slate-50/50 transition-colors">
                    <div className="w-12 text-center shrink-0">
                      <p className="text-xs text-slate-400">{s.shift_date && format(parseISO(s.shift_date), 'MMM')}</p>
                      <p className="text-2xl font-bold text-slate-700 leading-none">{s.shift_date && format(parseISO(s.shift_date), 'd')}</p>
                      <p className="text-xs text-slate-400">{s.shift_date && format(parseISO(s.shift_date), 'EEE')}</p>
                    </div>
                    <div className={clsx('flex-1 rounded-xl p-3', shiftColor(s.shift_type))}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-bold">{s.shift_type || 'Shift'}</p>
                        <span className={clsx('text-[10px] px-2 py-0.5 rounded-full font-medium', STATUS_COLOR[s.status] || STATUS_COLOR.pending)}>
                          {s.status}
                        </span>
                      </div>
                      <p className="text-xs mt-1 opacity-75">
                        <Clock size={11} className="inline mr-1"/>{fmtRange12(s.start_time, s.end_time)}
                      </p>
                      <p className="text-xs mt-0.5 opacity-75">
                        <Users size={11} className="inline mr-1"/>{s.staff_name || 'Unassigned'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* UPCOMING SHIFTS */}
          {tab === 'shifts' && (
            loading.shifts ? <Spinner /> :
            data.shifts === 'forbidden' ? <Forbidden /> :
            !data.shifts?.length ? <Empty msg="No upcoming shifts scheduled" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Time</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Staff</th>
                      <th className="px-4 py-3 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.shifts.map((s, i) => (
                      <tr key={s.id || i} className="hover:bg-slate-50/60">
                        <td className="px-4 py-3 font-medium text-slate-700">
                          {s.shift_date && format(parseISO(s.shift_date), 'EEE d MMM yyyy')}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {fmtRange12(s.start_time, s.end_time)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={clsx('text-xs px-2 py-0.5 rounded-lg font-medium', shiftColor(s.shift_type))}>
                            {s.shift_type || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{s.staff_name || 'Unassigned'}</td>
                        <td className="px-4 py-3">
                          <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium', STATUS_COLOR[s.status] || STATUS_COLOR.pending)}>
                            {s.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* STAFF */}
          {tab === 'staff' && (
            loading.staff ? <Spinner /> :
            data.staff === 'forbidden' ? <Forbidden /> :
            !data.staff?.length ? <Empty msg="No staff records found" /> : (
              <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                {data.staff.map((s, i) => (
                  <div key={s.id || i} className="border border-slate-100 rounded-2xl p-4 text-center hover:shadow-sm transition-shadow">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-sm mx-auto mb-3">
                      {s.first_name?.[0]}{s.last_name?.[0]}
                    </div>
                    <p className="font-semibold text-slate-800 text-sm">{s.first_name} {s.last_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5 capitalize">{s.job_title || s.role?.replace('_',' ')}</p>
                    <p className="text-xs text-primary-500 mt-1">{s.shift_count} shifts</p>
                  </div>
                ))}
              </div>
            )
          )}

          {/* NOTES */}
          {tab === 'notes' && (
            loading.notes ? <Spinner /> :
            data.notes === 'forbidden' ? <Forbidden /> :
            !data.notes?.length ? <Empty msg="No notes available" /> : (
              <div className="divide-y divide-slate-50">
                {data.notes.map((n, i) => (
                  <div key={n.id || i} className="p-4 hover:bg-slate-50/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-lg">
                        {n.note_type || 'Note'}
                      </span>
                      <span className="text-xs text-slate-400">
                        {n.note_date} · {n.author_name || 'Staff'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{n.content}</p>
                  </div>
                ))}
              </div>
            )
          )}

          {/* DOCUMENTS */}
          {tab === 'documents' && (
            loading.documents ? <Spinner /> :
            data.documents === 'forbidden' ? <Forbidden /> :
            !data.documents?.length ? <Empty msg="No documents shared" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left">File Name</th>
                      <th className="px-4 py-3 text-left">Category</th>
                      <th className="px-4 py-3 text-left">Uploaded</th>
                      <th className="px-4 py-3 text-left">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.documents.map((d, i) => (
                      <tr key={d.id || i} className="hover:bg-slate-50/60">
                        <td className="px-4 py-3 font-medium text-slate-700">
                          <div className="flex items-center gap-2">
                            <Paperclip size={13} className="text-slate-400 shrink-0"/>
                            {d.file_name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{d.category || '—'}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{d.uploaded_at?.split('T')[0]}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{d.expiry_date?.split('T')[0] || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* PROFILE */}
          {tab === 'profile' && (
            loading.profile ? <Spinner /> :
            data.profile === 'forbidden' ? <Forbidden /> :
            !data.profile ? <Empty /> : (
              <div className="p-6 space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-xl font-bold shadow">
                    {data.profile.first_name?.[0]}{data.profile.last_name?.[0]}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{data.profile.first_name} {data.profile.last_name}</h2>
                    <p className="text-sm text-slate-400 capitalize">{data.profile.client_type?.replace(/_/g,' ')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Date of Birth', value: data.profile.date_of_birth?.split('T')[0] },
                    { label: 'Phone', value: data.profile.phone },
                    { label: 'Address', value: data.profile.address },
                    { label: 'NDIS Number', value: data.profile.ndis_number },
                    { label: 'Primary Diagnosis', value: data.profile.primary_diagnosis },
                  ].map(f => f.value && (
                    <div key={f.label} className="bg-slate-50 rounded-xl p-3">
                      <p className="text-xs text-slate-400 mb-0.5">{f.label}</p>
                      <p className="text-sm font-medium text-slate-700">{f.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
